import React from 'react';
import LegalPageLayout, { LegalSub, LegalCallout, LegalContact } from '../components/legal/LegalPageLayout';

const MedicalDisclaimerPage = () => {
  const sections = [
    {
      id: 'general',
      title: 'General Medical Disclaimer',
      body: (
        <>
          <p>The information provided on this website is for informational and educational purposes only and is not intended to be a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of your physician or other qualified healthcare provider with any questions you may have regarding a medical condition or treatment.</p>
          <LegalCallout tone="warn">Never disregard professional medical advice or delay in seeking it because of information obtained from this website.</LegalCallout>
        </>
      ),
    },
    {
      id: 'professionals-only',
      title: 'For Healthcare Professionals Only',
      body: (
        <>
          <p>FlexiOss® products are <strong>Class III medical devices</strong> regulated by the Food and Drug Administration (FDA) of the Philippines. These products are intended for use by or under the supervision of licensed healthcare professionals only, including:</p>
          <ul className="premium-list space-y-2.5">
            <li>Orthopedic surgeons</li>
            <li>Maxillofacial surgeons</li>
            <li>Dentists and periodontists</li>
            <li>Veterinary surgeons</li>
            <li>Authorized hospital procurement personnel</li>
          </ul>
          <p>These products <strong>require professional training</strong> and must be used in accordance with the Instructions for Use (IFU) provided by the manufacturer.</p>
        </>
      ),
    },
    {
      id: 'certification',
      title: 'PH-FDA Certification and Compliance',
      body: (
        <>
          <p>All FlexiOss® products distributed in the Philippines are certified by the Philippine Food and Drug Administration (PH-FDA) under the following Certificate of Product Registration (CPR) numbers:</p>
          <ul className="premium-list space-y-2.5">
            <li><strong>FlexiOss® Ortho:</strong> CMDR-2025-04943A</li>
            <li><strong>FlexiOss® Dent:</strong> CMDR-2025-04943B</li>
            <li><strong>FlexiOss® Vet:</strong> CMDR-2025-04944A</li>
          </ul>
          <p>These certifications are issued in compliance with Republic Act No. 9711 (Food and Drug Administration Act of 2009) and FDA Circular No. 2020-004 (Guidelines on the Registration of Medical Devices).</p>
        </>
      ),
    },
    {
      id: 'intended-use',
      title: 'Intended Use and Indications',
      body: (
        <>
          <p>FlexiOss® bone regenerative products are indicated for filling and reconstruction of bone defects in orthopedic, dental, maxillofacial, and veterinary procedures. Each product line has specific indications:</p>
          <LegalSub>FlexiOss® Ortho</LegalSub>
          <p>Comminuted fractures, long bone defects, trauma surgery, bone tissue regeneration (max 7 cm defects).</p>
          <LegalSub>FlexiOss® Dent</LegalSub>
          <p>Post-extraction augmentation, implant site preparation, maxillofacial reconstruction, periodontal defects.</p>
          <LegalSub>FlexiOss® Vet</LegalSub>
          <p>Veterinary bone defects in dogs, cats, and horses; dental extractions; oronasal fistulae; EOTRH syndrome.</p>
          <LegalCallout tone="warn">Always refer to the complete Instructions for Use (IFU) document for detailed indications, contraindications, warnings, and precautions.</LegalCallout>
        </>
      ),
    },
    {
      id: 'contraindications',
      title: 'Contraindications and Warnings',
      body: (
        <>
          <LegalCallout tone="danger">FlexiOss® products are contraindicated in the following situations:</LegalCallout>
          <ul className="premium-list space-y-2.5">
            <li>Active or latent infections at the implantation site</li>
            <li>Severe systemic infections or sepsis</li>
            <li>Inadequate soft tissue coverage over the defect</li>
            <li>Patients with known hypersensitivity to hydroxyapatite or curdlan polymer</li>
            <li>Insufficient blood supply to the surgical site</li>
            <li>Metabolic bone diseases that impair healing (e.g., uncontrolled osteoporosis)</li>
          </ul>
          <LegalSub>Special Precautions</LegalSub>
          <ul className="premium-list space-y-2.5">
            <li>Use with caution in immunocompromised patients</li>
            <li>Not recommended for load-bearing applications without adequate fixation</li>
            <li>Pregnant or breastfeeding women should consult their physician before treatment</li>
            <li>Veterinary products: follow species-specific dosing and application guidelines</li>
          </ul>
        </>
      ),
    },
    {
      id: 'adverse-events',
      title: 'Adverse Events Reporting',
      body: (
        <>
          <p>Healthcare professionals and patients/caregivers are encouraged to report any suspected adverse events, product defects, or quality issues to:</p>
          <LegalSub>Fortschritt Medical Supplies Inc.</LegalSub>
          <p>Email: <a href="mailto:fms.medequip@gmail.com">fms.medequip@gmail.com</a> · Phone: (02) 8478 8009</p>
          <LegalSub>Philippine FDA — CDRRHR</LegalSub>
          <p>Adverse Event Reporting Hotline: 1-800-10-665-1527 (toll-free) · Email: <a href="mailto:cdrcomplaint@fda.gov.ph">cdrcomplaint@fda.gov.ph</a></p>
        </>
      ),
    },
    {
      id: 'accuracy',
      title: 'Product Information Accuracy',
      body: (
        <>
          <p>While we strive to provide accurate and up-to-date information, product specifications, indications, and regulatory status are subject to change. Healthcare professionals should:</p>
          <ul className="premium-list space-y-2.5">
            <li>Always refer to the most current Instructions for Use (IFU) document included with each product</li>
            <li>Verify PH-FDA certification status before purchase or use</li>
            <li>Contact our technical support team for the latest product information</li>
            <li>Check for product recalls or safety alerts on the PH-FDA website</li>
          </ul>
        </>
      ),
    },
    {
      id: 'no-relationship',
      title: 'No Doctor-Patient Relationship',
      body: (
        <p>Use of this website and communication with Fortschritt Medical Supplies Inc. does not create a doctor-patient relationship. We are a medical device distributor, not a healthcare provider. Any medical decisions should be made in consultation with a qualified healthcare professional.</p>
      ),
    },
    {
      id: 'liability',
      title: 'Limitation of Liability',
      body: (
        <>
          <p>To the fullest extent permitted by Philippine law, Fortschritt Medical Supplies Inc. shall not be liable for any damages arising from the use or misuse of information on this website, or from the use of FlexiOss® products in a manner inconsistent with the approved indications and Instructions for Use.</p>
          <p>Healthcare professionals assume full responsibility for proper patient selection, surgical technique, and post-operative care.</p>
        </>
      ),
    },
    {
      id: 'contact',
      title: 'Contact for Medical Inquiries',
      body: <LegalContact intro="For technical support, product information, or medical inquiries, please contact:" mobile="0917-579-8655 / 0917-579-3656" />,
    },
  ];

  return (
    <LegalPageLayout
      testId="medical-disclaimer-page"
      title="Medical Disclaimer"
      subtitle="Important safety, indication and usage information for FlexiOss® Class III medical devices."
      accent="#fbbf24"
      sections={sections}
    />
  );
};

export default MedicalDisclaimerPage;
